import { useOnlineStatusContext } from "../../context/OnlineStatusContext";
import { formatRelativeTime } from "../../utils/relativeTime";
import styles from "./StatusIndicator.module.scss";

interface StatusIndicatorProps {
  userId: string;
  /** "dot" for avatar overlay, "full" for profile header with text */
  variant: "dot" | "full";
  className?: string;
}

export default function StatusIndicator({
  userId,
  variant,
  className,
}: StatusIndicatorProps) {
  const { getStatus, hasStatus } = useOnlineStatusContext();
  const { isOnline, lastSeenAt } = getStatus(userId);

  // Don't render until we have actual status data to avoid flash of "Offline"
  if (!hasStatus(userId)) {
    // For "full" variant, render an invisible placeholder to reserve space and prevent layout shift
    if (variant === "full") {
      const classNames = [styles.full, styles.placeholder, className].filter(Boolean).join(" ");
      return (
        <span className={classNames} aria-hidden="true">
          <span className={styles.dot} />
          <span className={styles.text}>&nbsp;</span>
        </span>
      );
    }
    return null;
  }

  const dotClass = [
    styles.dot,
    isOnline ? styles.online : styles.offline,
  ].join(" ");

  if (variant === "dot") {
    const classNames = [styles.dotWrapper, className].filter(Boolean).join(" ");
    return (
      <span className={classNames}>
        <span className={dotClass} aria-label={isOnline ? "Online" : "Offline"} />
      </span>
    );
  }

  // variant === "full"
  const classNames = [styles.full, className].filter(Boolean).join(" ");

  let statusText: string;
  if (isOnline) {
    statusText = "Online";
  } else if (lastSeenAt) {
    statusText = `Last seen ${formatRelativeTime(lastSeenAt)}`;
  } else {
    statusText = "Offline";
  }

  return (
    <span className={classNames}>
      <span className={dotClass} aria-hidden="true" />
      <span className={styles.text}>{statusText}</span>
    </span>
  );
}
